import type { CSSProperties, FC } from "react";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { enqueueSnackbar } from "notistack";
import { Link } from "react-router-dom";

import { Button } from "@src/components";
import { APP_ROUTES } from "@src/core/constants";
import { ApiTokens } from "@src/core/services";
import { StyledTextField } from "@src/core/styles";
import type { ApiTokenListResponse } from "@src/core/types/settings/tokens.types";

const cardStyle: CSSProperties = {
  padding: "20px",
  borderRadius: "12px",
  background: "#f8f9fa",
  border: "1px solid #e0e0e0",
};

const formatDate = (value: string | null) => {
  if (!value) return "ещё не использовался";
  try {
    return new Date(value).toLocaleString("ru-RU");
  } catch {
    return value;
  }
};

export const ApiTokensPanel: FC = () => {
  const queryClient = useQueryClient();
  const [name, setName] = useState("Cursor");
  const [plainToken, setPlainToken] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["api-tokens"],
    queryFn: () => ApiTokens.list(),
  });

  const createMutation = useMutation(ApiTokens.create, {
    onSuccess: (response) => {
      setPlainToken(response.data.token);
      setName("Cursor");
      queryClient.invalidateQueries({ queryKey: ["api-tokens"] });
      enqueueSnackbar("Токен создан. Скопируйте его сейчас — повторно он не покажется.", {
        variant: "reportComplete",
        className: "success",
        preventDuplicate: true,
      });
    },
    onError: (error: AxiosError<{ errors?: { name?: string[] }; message?: string }>) => {
      const message =
        error.response?.data.errors?.name?.[0] ||
        error.response?.data.message ||
        "Не удалось создать токен";
      enqueueSnackbar(message, {
        variant: "reportComplete",
        className: "error",
        preventDuplicate: true,
      });
    },
  });

  const revokeMutation = useMutation(ApiTokens.revoke, {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["api-tokens"] });
      enqueueSnackbar("Токен отозван", {
        variant: "reportComplete",
        className: "success",
        preventDuplicate: true,
      });
    },
  });

  const tokens: ApiTokenListResponse | undefined = data?.data;
  const max = tokens?.max ?? 5;

  const handleCopy = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      enqueueSnackbar("Скопировано", {
        variant: "reportComplete",
        className: "success",
        preventDuplicate: true,
      });
    } catch {
      enqueueSnackbar("Не удалось скопировать", {
        variant: "reportComplete",
        className: "error",
        preventDuplicate: true,
      });
    }
  };

  return (
    <div style={{ ...cardStyle, marginBottom: "32px" }}>
      <div
        style={{
          fontSize: "12px",
          color: "#888",
          textTransform: "uppercase",
          letterSpacing: "0.5px",
          marginBottom: "8px",
        }}
      >
        API-токены для агентов
      </div>
      <p style={{ margin: "0 0 16px", fontSize: "14px", color: "#666", lineHeight: 1.5 }}>
        Токен нужен Cursor, Claude и другим агентам, чтобы создавать моки через MCP.
        Секрет показывается только один раз. Подключение:{" "}
        <Link to={APP_ROUTES.CONNECT}>страница MCP</Link>.
      </p>

      {plainToken && (
        <div
          style={{
            marginBottom: "16px",
            padding: "12px",
            borderRadius: "8px",
            background: "#fff",
            border: "1px solid #c8e6c9",
          }}
        >
          <div style={{ fontSize: "12px", color: "#2e7d32", marginBottom: "8px" }}>
            Новый токен — сохраните его. Повторно увидеть его нельзя.
          </div>
          <code style={{ fontSize: "13px", wordBreak: "break-all", color: "#1976d2" }}>
            {plainToken}
          </code>
          <div style={{ marginTop: "8px" }}>
            <Button
              text="Копировать токен"
              type="button"
              variant="outlined"
              borderradius="L"
              onClick={() => handleCopy(plainToken)}
            />
          </div>
        </div>
      )}

      <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", alignItems: "flex-start", marginBottom: "16px" }}>
        <StyledTextField
          variant="outlined"
          label="Имя токена"
          value={name}
          onChange={(event) => setName(event.target.value)}
          style={{ minWidth: "220px" }}
        />
        <div style={{ paddingTop: "8px" }}>
          <Button
            text="Выпустить токен"
            type="button"
            borderradius="L"
            disabled={createMutation.isLoading || !name.trim()}
            onClick={() => createMutation.mutate(name.trim())}
          />
        </div>
      </div>

      {isLoading && <div style={{ fontSize: "14px", color: "#888" }}>Загрузка токенов…</div>}

      {!isLoading && (
        <div style={{ fontSize: "13px", color: "#888", marginBottom: "8px" }}>
          {tokens?.items.length ?? 0} / {max}
        </div>
      )}

      {(tokens?.items ?? []).map((token) => (
        <div
          key={token.id}
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: "12px",
            alignItems: "center",
            padding: "10px 0",
            borderTop: "1px solid #eee",
          }}
        >
          <div>
            <div style={{ fontWeight: 600, color: "#333" }}>{token.name}</div>
            <div style={{ fontSize: "12px", color: "#888" }}>
              создан {formatDate(token.created_at)} · {formatDate(token.last_used_at)}
            </div>
          </div>
          <Button
            text="Отозвать"
            type="button"
            variant="outlined"
            borderradius="L"
            disabled={revokeMutation.isLoading}
            onClick={() => revokeMutation.mutate(token.id)}
          />
        </div>
      ))}
    </div>
  );
};
